import { useEffect, useMemo } from 'react';
import { useAppFlags } from '../utils/featureFlags';

/**
 * Column ids that hold the Actions menu. Manage Certifications names the column
 * `action`. Every other table names it `actions`.
 */
const ACTIONS_COLUMN_IDS = ['actions', 'action'];

/**
 * Column ids that stay to the left of the Actions column. A select-all checkbox
 * keeps the first slot.
 */
const LEADING_COLUMN_IDS = ['checkbox'];

/**
 * localStorage key that records a lift. Its value is the position the Actions
 * column held before the lift, so a flag turned off later can put it back.
 * Older records hold the string `true`, which restores to the header position.
 */
const migrationKey = (name) => `${name}_actions-column-first`;

const findActionsId = (ids) =>
  ids.find((id) => ACTIONS_COLUMN_IDS.includes(id));

/**
 * Orders the header ids by the stored map, and falls back to the header order
 * for an id the map does not mention.
 */
const arrangeByStoredOrder = ({ headerIds, storedOrder }) => {
  const naturalIndex = {};
  headerIds.forEach((id, index) => {
    naturalIndex[id] = index;
  });

  return [...headerIds].sort(
    (a, b) =>
      (storedOrder?.[a] ?? naturalIndex[a]) -
        (storedOrder?.[b] ?? naturalIndex[b]) ||
      naturalIndex[a] - naturalIndex[b],
  );
};

/**
 * Stored ids that the current header set does not render, in their stored
 * order. A column can be hidden for a while (a permission, a flag, the ADA
 * pair). The chooser refuses to drag an id the stored map does not mention, so
 * every write carries these along, after the rendered columns.
 */
const unrenderedStoredIds = ({ headerIds, storedOrder }) =>
  Object.keys(storedOrder ?? {})
    .filter((id) => !headerIds.includes(id))
    .sort((a, b) => storedOrder[a] - storedOrder[b]);

const toOrderMap = (ids) => {
  const order = {};
  ids.forEach((id, index) => {
    order[id] = index;
  });
  return order;
};

/**
 * Builds the `initialColumnsOrder` map that `useTableManageColumns` sorts both
 * the table and the column filter by.
 *
 * The table library keeps the column order in a `${name}_column-order`
 * localStorage map of id to position. It seeds that map only when the map is
 * empty, and the column filter refuses to drag any id the map does not mention.
 * So the order map, not a transform of the rendered headers, is the only place
 * that moves the Actions column for both the table and the filter.
 *
 * Three cases:
 * 1. No stored order, or an unreadable one. Write the default with Actions
 *    first.
 * 2. A stored order from before the flag. Lift Actions to the front once, and
 *    keep the user's arrangement of every other column.
 * 3. A stored order that already migrated. Leave the positions alone, so a
 *    drag survives a reload. Backfill only the ids the map is missing, because
 *    a missing id cannot be dragged.
 *
 * Also returns `previousIndex`, the position Actions held before the lift, so
 * the caller can record it for a later restore.
 */
export const buildActionsFirstOrder = ({
  headerIds = [],
  storedOrder = null,
  hasMigrated = false,
}) => {
  if (!headerIds.length) {
    return {
      initialColumnsOrder: {},
      shouldForceOverwrite: false,
      previousIndex: null,
    };
  }

  const hasMissingIds = storedOrder
    ? headerIds.some((id) => storedOrder[id] === undefined)
    : true;
  const shouldLiftActions = !storedOrder || !hasMigrated;
  const shouldForceOverwrite = shouldLiftActions || hasMissingIds;

  const arranged = arrangeByStoredOrder({ headerIds, storedOrder });
  const actionsId = findActionsId(arranged);
  const previousIndex = actionsId ? arranged.indexOf(actionsId) : null;

  if (!shouldForceOverwrite) {
    return {
      initialColumnsOrder: toOrderMap(headerIds),
      shouldForceOverwrite: false,
      previousIndex,
    };
  }

  if (shouldLiftActions && actionsId) {
    arranged.splice(previousIndex, 1);
    const targetIndex = LEADING_COLUMN_IDS.includes(arranged[0]) ? 1 : 0;
    arranged.splice(targetIndex, 0, actionsId);
  }

  return {
    initialColumnsOrder: toOrderMap([
      ...arranged,
      ...unrenderedStoredIds({ headerIds, storedOrder }),
    ]),
    shouldForceOverwrite: true,
    previousIndex,
  };
};

/**
 * Builds the order that undoes a lift: Actions goes back to `previousIndex`
 * and every other column keeps its stored position. A record that is not a
 * number, such as the older `true`, restores Actions to its header position.
 */
export const buildRestoredOrder = ({
  headerIds = [],
  storedOrder = null,
  previousIndex,
}) => {
  if (!storedOrder || !headerIds.length) {
    return { initialColumnsOrder: undefined, shouldForceOverwrite: false };
  }

  const arranged = arrangeByStoredOrder({ headerIds, storedOrder });
  const actionsId = findActionsId(arranged);

  // No Actions column in this header set means nothing to put back. Do not
  // rewrite the stored map for nothing.
  if (!actionsId) {
    return { initialColumnsOrder: undefined, shouldForceOverwrite: false };
  }

  arranged.splice(arranged.indexOf(actionsId), 1);
  const fallbackIndex = headerIds.indexOf(actionsId);
  const restoreIndex = Number.isInteger(previousIndex)
    ? Math.min(Math.max(previousIndex, 0), arranged.length)
    : Math.min(fallbackIndex, arranged.length);
  arranged.splice(restoreIndex, 0, actionsId);

  return {
    initialColumnsOrder: toOrderMap([
      ...arranged,
      ...unrenderedStoredIds({ headerIds, storedOrder }),
    ]),
    shouldForceOverwrite: true,
  };
};

const readStoredOrder = (name) => {
  try {
    const raw = localStorage.getItem(`${name}_column-order`);
    const parsed = raw ? JSON.parse(raw) : null;

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const readMigrationRecord = (name) => {
  try {
    const raw = localStorage.getItem(migrationKey(name));

    return { present: raw !== null, previousIndex: Number.parseInt(raw, 10) };
  } catch {
    return { present: false, previousIndex: Number.NaN };
  }
};

const writeMigrationRecord = (name, previousIndex) => {
  try {
    localStorage.setItem(migrationKey(name), String(previousIndex));
  } catch {
    // A browser that refuses the write repeats the one-time lift on the next
    // load. That is the old behavior, so there is nothing to recover here.
  }
};

const clearMigrationRecord = (name) => {
  try {
    localStorage.removeItem(migrationKey(name));
  } catch {
    // Same as above: the restore repeats on the next load.
  }
};

/**
 * Returns the `initialColumnsOrder` and `forceOverwrite` to hand to
 * `useTableManageColumns` so the Actions column sits first in both the table
 * and the column filter while the `moveActionColumnsToLeft` flag is on.
 *
 * While the flag is off, returns the caller's own values unchanged, so a
 * caller that already seeds a column order keeps it. The one exception is a
 * browser that already ran the lift: it puts Actions back where it was, once,
 * and clears the record, so the flag-off layout matches a browser that never
 * saw the flag.
 */
const useActionsFirstColumnOrder = ({
  name,
  headers,
  initialColumnsOrder: baseColumnsOrder,
  forceOverwrite: baseForceOverwrite = false,
}) => {
  const { moveActionColumnsToLeft = false } = useAppFlags();

  const headerIds = useMemo(
    () => (headers ?? []).map(({ id }) => id),
    [headers],
  );

  const plan = useMemo(() => {
    if (!headerIds.length) {
      return {
        initialColumnsOrder: baseColumnsOrder,
        shouldForceOverwrite: baseForceOverwrite,
        record: null,
      };
    }

    const storedOrder = readStoredOrder(name);
    const migration = readMigrationRecord(name);

    if (!moveActionColumnsToLeft) {
      if (!migration.present) {
        return {
          initialColumnsOrder: baseColumnsOrder,
          shouldForceOverwrite: baseForceOverwrite,
          record: null,
        };
      }

      const restored = buildRestoredOrder({
        headerIds,
        storedOrder,
        previousIndex: migration.previousIndex,
      });

      // Nothing stored means nothing to put back. Keep the caller's values and
      // drop the stale record.
      return restored.shouldForceOverwrite
        ? { ...restored, record: 'clear' }
        : {
            initialColumnsOrder: baseColumnsOrder,
            shouldForceOverwrite: baseForceOverwrite,
            record: 'clear',
          };
    }

    const { initialColumnsOrder, shouldForceOverwrite, previousIndex } =
      buildActionsFirstOrder({
        headerIds,
        storedOrder,
        hasMigrated: migration.present,
      });

    return {
      initialColumnsOrder,
      shouldForceOverwrite,
      // Keep the first record. A later render must not overwrite the position
      // Actions held before the lift with its lifted position.
      record: migration.present ? null : previousIndex,
    };
  }, [
    moveActionColumnsToLeft,
    name,
    headerIds,
    baseColumnsOrder,
    baseForceOverwrite,
  ]);

  useEffect(() => {
    if (plan.record === 'clear') {
      clearMigrationRecord(name);
    } else if (plan.record !== null && plan.record !== undefined) {
      writeMigrationRecord(name, plan.record);
    }
  }, [name, plan.record]);

  return useMemo(
    () => ({
      initialColumnsOrder: plan.initialColumnsOrder,
      shouldForceOverwrite: plan.shouldForceOverwrite,
    }),
    [plan.initialColumnsOrder, plan.shouldForceOverwrite],
  );
};

export default useActionsFirstColumnOrder;
